import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useReferralToken } from "~/features/Referral/useReferralToken";
import { useAuthN } from "~/hooks/useAuthN";
import { trackEmail, trackPartnerEmail } from "~/OnboardingApp/events";
import { useNav } from "~/OnboardingApp/features/Nav";
import { usePartner } from "~/OnboardingApp/hooks";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { clearQrCodeKey } from "~/OnboardingApp/hooks/usePartner";
import { useOnboardingCreateMutation } from "~/store/api";

const validations = {
  acceptedTerms: Yup.boolean()
    .required("Required")
    .oneOf([true], "You must accept the terms of service."),
  acceptedAdditionalTerms: Yup.boolean()
    .required("Required")
    .oneOf([true], "You must accept the terms of service."),
  email: Yup.string().email("Invalid email address").required("Required"),
};
const validationSchema = Yup.object(validations);

interface Values {
  acceptedAdditionalTerms: boolean;
  acceptedTerms: boolean;
  email: string;
  firstName: string;
  lastName: string;
  referralToken: string | null;
  partnerSlug: string;
  qrCodeKey: string;
}

interface UseSignUpForm {
  errorMessage: string | null;
  formik: ReturnType<typeof useFormik<Values>>;
}

export const useSignUpForm = (): UseSignUpForm => {
  const [apiOnboardingCreate] = useOnboardingCreateMutation();
  const referralToken = useReferralToken();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    handleSetOnboarding,
    acceptedAdditionalTerms,
    acceptedTerms,
    email,
    firstName,
    lastName,
  } = useOnboarding();
  const { handleSetUser } = useAuthN();
  const { goToNext } = useNav();
  const { partnerSlug, qrCodeKey } = usePartner();
  const track = partnerSlug ? trackPartnerEmail : trackEmail;

  const initialValues: Values = {
    acceptedAdditionalTerms: acceptedAdditionalTerms || false,
    acceptedTerms: acceptedTerms || false,
    email: email || "",
    firstName: firstName || "",
    lastName: lastName || "",
    referralToken: null,
    partnerSlug: partnerSlug || "",
    qrCodeKey: qrCodeKey || "",
  };

  const handleSubmit = async (values: Values) => {
    try {
      track.submit();
      const responseBody = await apiOnboardingCreate(values).unwrap();
      track.submitSuccess();
      clearQrCodeKey();

      const user = responseBody.data.attributes;
      handleSetUser(user);

      const onboarding = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        onboardingSource: values.partnerSlug,
      };
      handleSetOnboarding(onboarding);
      goToNext(onboarding);
    } catch (err: any) {
      if (err?.data?.email) {
        setErrorMessage("This email is not available");
        Sentry.captureMessage("Email already in use", { extra: err });
      } else {
        setErrorMessage("An error has occurred; please try again later");
        Sentry.captureException(err);
      }
    }
  };

  const formik = useFormik({
    initialValues: { ...initialValues, referralToken },
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
  });

  return {
    errorMessage,
    formik,
  };
};
